import { getSessionHeaders } from '../../lib/serverSession';

export type GalleryItem = {
  kind?: 'image' | 'video';
  mediaId?: string;
  type?: 'image' | 'video';
  imagePath?: string | null;
  thumbPath?: string | null;
  imageUrl?: string | null;
  fullUrl?: string | null;
  imageAlt?: string;
  videoPath?: string | null;
  posterPath?: string | null;
  videoUrl?: string | null;
  posterUrl?: string | null;
};

const IMAGE_LIMIT = 20 * 1024 * 1024;
const VIDEO_LIMIT = 40 * 1024 * 1024;

async function api(operation: string, data: Record<string, unknown>) {
  const response = await fetch('/api/admin/announcements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({ operation, data }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) throw new Error(body?.error || 'The file could not be saved.');
  return body;
}

function fitCanvas(source: CanvasImageSource, width: number, height: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not prepare the picture.');
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('This browser could not prepare the picture.'))), type, quality);
  });
}

async function pictureBlobs(file: File) {
  const bitmap = await createImageBitmap(file);
  try {
    const large = await canvasBlob(fitCanvas(bitmap, bitmap.width, bitmap.height, 2000), 'image/webp', 0.86);
    const thumb = await canvasBlob(fitCanvas(bitmap, bitmap.width, bitmap.height, 480), 'image/webp', 0.8);
    return { large, thumb };
  } finally {
    bitmap.close();
  }
}

async function posterBlob(file: File) {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  const url = URL.createObjectURL(file);
  try {
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('poster'));
    });
    video.currentTime = Math.min(0.4, (video.duration || 1) / 2);
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
    });
    return await canvasBlob(fitCanvas(video, video.videoWidth || 640, video.videoHeight || 360, 480), 'image/jpeg', 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function putSigned(signedUrl: string, blob: Blob, contentType: string) {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!response.ok) throw new Error('The file could not be saved.');
}

export async function saveAnnouncementMedia(file: File): Promise<GalleryItem> {
  if (file.type.startsWith('image/')) {
    if (file.size > IMAGE_LIMIT) throw new Error('That picture is too large.');
    const prepared = await api('prepare_media', { kind: 'image' });
    const blobs = await pictureBlobs(file);
    await putSigned(prepared.full.signedUrl, blobs.large, 'image/webp');
    await putSigned(prepared.thumb.signedUrl, blobs.thumb, 'image/webp');
    const saved = await api('complete_media', {
      mediaId: prepared.mediaId,
      kind: 'image',
      imagePath: prepared.full.path,
      thumbPath: prepared.thumb.path,
    });
    return saved.element;
  }
  if (file.type === 'video/mp4' || file.type === 'video/webm') {
    if (file.size > VIDEO_LIMIT) throw new Error('That video must be 40 MB or smaller.');
    const prepared = await api('prepare_media', { kind: 'video', contentType: file.type });
    await putSigned(prepared.video.signedUrl, file, file.type);
    let posterPath: string | null = null;
    try {
      const poster = await posterBlob(file);
      await putSigned(prepared.poster.signedUrl, poster, 'image/jpeg');
      posterPath = prepared.poster.path;
    } catch {
      posterPath = null;
    }
    const saved = await api('complete_media', {
      mediaId: prepared.mediaId,
      kind: 'video',
      videoPath: prepared.video.path,
      posterPath,
    });
    return saved.element;
  }
  throw new Error('Use a JPG, PNG, WEBP, GIF, MP4, or WEBM file.');
}

export async function listAnnouncementMedia(): Promise<GalleryItem[]> {
  const body = await api('list_media', {});
  return Array.isArray(body.media) ? body.media : [];
}
